import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import Button from '@/components-latest/primitives/Button'
import Input from '@/components-latest/primitives/Controls/Input'
import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import CommonModal from '@/components-latest/shared/Modals/CommonModal'
import { isErrorType } from '@/config/http/http.helpers'
import { useInitStoreWalletWithdrawMutation } from '@/ducks/billing/api/billing.api'
import {
  copyWalletTextToClipboard,
  createWithdrawIdempotencyKey,
  formatWalletMoneyAmount,
  getFundingPaymentMethodLabel,
  getFundingUnavailableMessageKey,
  getWithdrawFailureMessageKey,
  getWithdrawTimeoutMessageKey,
  isWithdrawRecoveredSuccess,
  isWithdrawRequestAmbiguousError,
  selectWithdrawPaymentMethod,
  validateWithdrawAmount
} from '@/ducks/billing/billing.wallet-funding'
import {
  StoreFundingAttempt,
  StoreFundingMethodsResponse
} from '@/ducks/billing/billing.wallet-funding.types'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'

import Styles from './RequestPayoutModal.module.scss'

type ModalStep = 'amount' | 'methods' | 'instructions'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  balanceAmount: number
  currency: string
  fundingMethods?: StoreFundingMethodsResponse | null
  isFundingMethodsLoading?: boolean
  fundingMethodsError?: unknown
  onSuccess?: (result?: {
    fundingAttemptId: string
    amount: number
    currency: string
  }) => void
}

const RequestPayoutModal = ({
  open,
  onOpenChange,
  balanceAmount,
  currency,
  fundingMethods,
  isFundingMethodsLoading = false,
  fundingMethodsError,
  onSuccess
}: Props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const withdrawIdempotencyKeyRef = useRef<string | null>(null)

  const [step, setStep] = useState<ModalStep>('amount')
  const [amount, setAmount] = useState('')
  const [validationErrorKey, setValidationErrorKey] = useState<string | null>(
    null
  )
  const [attempt, setAttempt] = useState<StoreFundingAttempt | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const paymentMethod = useMemo(
    () => selectWithdrawPaymentMethod(fundingMethods),
    [fundingMethods]
  )

  const paymentMethodLabel = useMemo(
    () => getFundingPaymentMethodLabel(paymentMethod),
    [paymentMethod]
  )

  const unavailableMessageKey = useMemo(
    () =>
      getFundingUnavailableMessageKey({
        fundingMethods,
        fundingMethodsError,
        isFundingMethodsLoading
      }),
    [fundingMethods, fundingMethodsError, isFundingMethodsLoading]
  )

  const [initWithdraw, { isLoading: isSubmitting }] =
    useInitStoreWalletWithdrawMutation()

  const resetState = useCallback(() => {
    setStep('amount')
    setAmount('')
    setValidationErrorKey(null)
    setAttempt(null)
    setSubmitError(null)
    withdrawIdempotencyKeyRef.current = null
  }, [])

  useEffect(() => {
    if (!open) {
      resetState()
    }
  }, [open, resetState])

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      onOpenChange(nextOpen)
      if (!nextOpen) {
        resetState()
      }
    },
    [onOpenChange, resetState]
  )

  const handleCopyCode = useCallback(async () => {
    const code = attempt?.nextAction?.instructions?.code
    if (!code) return

    const copied = await copyWalletTextToClipboard(code)
    dispatch(
      addSystemNotification({
        message: t(
          copied
            ? 'billing.wallet.payout.code-copied'
            : 'billing.wallet.payout.copy-failed'
        ),
        intent: copied ? 'info' : 'error',
        duration: 2000
      })
    )
  }, [attempt?.nextAction?.instructions?.code, dispatch, t])

  const notifyPayoutSuccess = useCallback(
    (result?: StoreFundingAttempt | null, submittedAmount?: number) => {
      const fundingAttemptId = String(result?.fundingAttemptId || '').trim()
      const resolvedAmount =
        submittedAmount ?? Math.abs(Number(result?.amount ?? amount))

      if (!fundingAttemptId) {
        onSuccess?.()
        return
      }

      onSuccess?.({
        fundingAttemptId,
        amount: resolvedAmount,
        currency
      })
    },
    [amount, currency, onSuccess]
  )

  const showSubmitError = useCallback(
    (message: string) => {
      setSubmitError(message)
      dispatch(
        addSystemNotification({
          message,
          intent: 'error',
          duration: 5000
        })
      )
    },
    [dispatch]
  )

  const resolveWithdrawAttempt = useCallback(
    async (idempotencyKey: string) => {
      return initWithdraw({
        amount: formatWalletMoneyAmount(Number(amount)),
        paymentMethodId: paymentMethod?.paymentMethodId,
        idempotencyKey
      }).unwrap()
    },
    [amount, initWithdraw, paymentMethod?.paymentMethodId]
  )

  const handleWithdrawResult = useCallback(
    (result: StoreFundingAttempt, options?: { timeoutRecovered?: boolean }) => {
      setAttempt(result)

      if (options?.timeoutRecovered) {
        const timeoutMessage = t(getWithdrawTimeoutMessageKey(result))
        dispatch(
          addSystemNotification({
            message: timeoutMessage,
            intent: isWithdrawRecoveredSuccess(result) ? 'info' : 'error',
            duration: 6000
          })
        )
        setSubmitError(timeoutMessage)
        notifyPayoutSuccess(result)

        if (
          isWithdrawRecoveredSuccess(result) &&
          result.nextAction?.type === 'SHOW_AGENT_INSTRUCTIONS'
        ) {
          setStep('instructions')
        }

        return
      }

      const failureMessageKey = getWithdrawFailureMessageKey(result)
      if (failureMessageKey) {
        const failureMessage = t(failureMessageKey)
        showSubmitError(failureMessage)
        notifyPayoutSuccess(result)
        handleOpenChange(false)
        return
      }

      if (result.nextAction?.type === 'SHOW_AGENT_INSTRUCTIONS') {
        setStep('instructions')
        notifyPayoutSuccess(result)
        return
      }

      notifyPayoutSuccess(result)
    },
    [dispatch, handleOpenChange, notifyPayoutSuccess, showSubmitError, t]
  )

  const handleContinueAmount = useCallback(() => {
    const validationKey = validateWithdrawAmount({
      amount,
      balance: balanceAmount,
      method: paymentMethod
    })
    if (validationKey) {
      setValidationErrorKey(validationKey)
      return
    }

    setValidationErrorKey(null)
    setSubmitError(null)
    setStep('methods')
  }, [amount, balanceAmount, paymentMethod])

  const handleConfirmWithdraw = useCallback(async () => {
    const validationKey = validateWithdrawAmount({
      amount,
      balance: balanceAmount,
      method: paymentMethod
    })
    if (validationKey) {
      setValidationErrorKey(validationKey)
      setStep('amount')
      return
    }

    setValidationErrorKey(null)
    setSubmitError(null)

    const idempotencyKey =
      withdrawIdempotencyKeyRef.current ?? createWithdrawIdempotencyKey()
    withdrawIdempotencyKeyRef.current = idempotencyKey

    try {
      const result = await resolveWithdrawAttempt(idempotencyKey)
      handleWithdrawResult(result)
    } catch (error) {
      if (!isWithdrawRequestAmbiguousError(error)) {
        const message = isErrorType(error)
          ? error.message
          : t('billing.wallet.payout.errors.generic')
        setSubmitError(
          typeof message === 'string'
            ? message
            : t('billing.wallet.payout.errors.generic')
        )
        // Clear so a corrected retry (e.g. different amount) does not reuse this key.
        withdrawIdempotencyKeyRef.current = null
        return
      }

      try {
        const recovered = await resolveWithdrawAttempt(idempotencyKey)
        handleWithdrawResult(recovered, { timeoutRecovered: true })
      } catch (retryError) {
        const message = t('billing.wallet.payout.errors.timeout-failed')
        showSubmitError(message)
      }
    }
  }, [
    amount,
    balanceAmount,
    handleWithdrawResult,
    paymentMethod,
    resolveWithdrawAttempt,
    showSubmitError,
    t
  ])

  const amountBody = (
    <div className={Styles.body} data-testid="request-payout-amount-step">
      {isFundingMethodsLoading ? (
        <div className={Styles.loading}>
          <Spinner />
        </div>
      ) : unavailableMessageKey ? (
        <Text variant="nextBody" className={Styles.unavailableMessage}>
          {t(unavailableMessageKey)}
        </Text>
      ) : (
        <>
          <Text variant="nextBody" className={Styles.description}>
            {t('billing.wallet.payout.description')}
          </Text>
          <label className={Styles.field}>
            <Text variant="nextBody" weight="semiBold">
              {t('billing.wallet.payout.amount-label')}
            </Text>
            <div className={Styles.amountRow}>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={event => {
                  setAmount(event.target.value)
                  setValidationErrorKey(null)
                  setSubmitError(null)
                }}
                placeholder={formatWalletMoneyAmount(balanceAmount)}
                data-testid="request-payout-amount-input"
              />
              <Text variant="nextBody" weight="semiBold">
                {currency}
              </Text>
            </div>
          </label>
          <Text variant="nextCaption" className={Styles.hint}>
            {t('billing.wallet.payout.available-balance', {
              amount: formatWalletMoneyAmount(balanceAmount),
              currency
            })}
          </Text>
          {validationErrorKey && (
            <Text variant="nextCaption" className={Styles.error}>
              {t(validationErrorKey)}
            </Text>
          )}
          {submitError && (
            <Text variant="nextCaption" className={Styles.error}>
              {submitError}
            </Text>
          )}
        </>
      )}
    </div>
  )

  const methodsBody = (
    <div className={Styles.body} data-testid="request-payout-methods-step">
      <Text variant="nextBody" className={Styles.description}>
        {t('billing.wallet.payout.methods-description')}
      </Text>
      <div className={Styles.methodsSection}>
        <Text variant="nextBody" weight="semiBold">
          {t('billing.wallet.payout.available-methods-title')}
        </Text>
        {paymentMethod ? (
          <div
            className={Styles.methodCard}
            data-testid="request-payout-payment-method">
            <div className={Styles.paymentMethodValue}>
              {paymentMethod.iconUrl ? (
                <img
                  src={paymentMethod.iconUrl}
                  alt=""
                  className={Styles.paymentMethodIcon}
                />
              ) : null}
              <div className={Styles.methodCardText}>
                <Text variant="nextBody" weight="semiBold">
                  {paymentMethodLabel}
                </Text>
                {paymentMethod.presentation?.summary?.subtitle ? (
                  <Text variant="nextCaption" className={Styles.methodSubtitle}>
                    {paymentMethod.presentation.summary.subtitle}
                  </Text>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>
      <div className={Styles.summaryCard}>
        <div className={Styles.summaryRow}>
          <Text variant="nextBody" className={Styles.summaryLabel}>
            {t('billing.wallet.payout.amount-label')}
          </Text>
          <Text variant="nextBody" weight="semiBold">
            {formatWalletMoneyAmount(Number(amount))} {currency}
          </Text>
        </div>
      </div>
      {paymentMethod?.presentation?.instructions ? (
        <Text variant="nextCaption" className={Styles.hint}>
          {paymentMethod.presentation.instructions}
        </Text>
      ) : null}
      {submitError && (
        <Text variant="nextCaption" className={Styles.error}>
          {submitError}
        </Text>
      )}
    </div>
  )

  const instructionsBody = (
    <div className={Styles.body} data-testid="request-payout-instructions-step">
      <Text variant="heading-05" weight="semiBold">
        {attempt?.nextAction?.instructions?.title ||
          t('billing.wallet.payout.instructions-title')}
      </Text>
      <Text variant="nextBody" className={Styles.description}>
        {attempt?.nextAction?.instructions?.body ||
          t('billing.wallet.payout.instructions-body')}
      </Text>
      {attempt?.nextAction?.instructions?.code && (
        <div className={Styles.codeBlock}>
          <Text variant="nextCaption" weight="semiBold" className={Styles.code}>
            {attempt.nextAction.instructions.code}
          </Text>
          <Button
            variant="tertiary"
            size="smaller"
            className={Styles.copyButton}
            onClick={handleCopyCode}>
            {t('billing.wallet.payout.copy-code')}
          </Button>
        </div>
      )}
      <Text variant="nextCaption" className={Styles.hint}>
        {t('billing.wallet.payout.validity-hint')}
      </Text>
    </div>
  )

  const amountFooter = (
    <div className={Styles.footer}>
      <Button
        variant="secondary"
        shape="rounded"
        onClick={() => handleOpenChange(false)}>
        {t('common.modal.cancel')}
      </Button>
      <Button
        shape="rounded"
        disabled={Boolean(unavailableMessageKey) || isFundingMethodsLoading}
        onClick={handleContinueAmount}
        data-testid="request-payout-continue">
        {t('billing.wallet.payout.continue')}
      </Button>
    </div>
  )

  const methodsFooter = (
    <div className={Styles.footer}>
      <Button
        variant="secondary"
        shape="rounded"
        onClick={() => {
          setSubmitError(null)
          setStep('amount')
        }}>
        {t('common.cta.back')}
      </Button>
      <Button
        shape="rounded"
        disabled={!paymentMethod || isSubmitting}
        onClick={handleConfirmWithdraw}
        data-testid="request-payout-submit">
        {isSubmitting ? <Spinner /> : t('billing.wallet.payout.confirm')}
      </Button>
    </div>
  )

  const instructionsFooter = (
    <div className={Styles.footer}>
      <Button shape="rounded" onClick={() => handleOpenChange(false)}>
        {t('billing.wallet.payout.done')}
      </Button>
    </div>
  )

  const modalBody =
    step === 'amount'
      ? amountBody
      : step === 'methods'
        ? methodsBody
        : instructionsBody

  const modalFooter =
    step === 'amount'
      ? amountFooter
      : step === 'methods'
        ? methodsFooter
        : instructionsFooter

  return (
    <CommonModal
      open={open}
      onOpenChange={handleOpenChange}
      title={t('billing.wallet.request-payout')}
      body={modalBody}
      footer={modalFooter}
      width={480}
      onGoBack={
        step === 'methods'
          ? () => {
              setSubmitError(null)
              setStep('amount')
            }
          : step === 'instructions'
            ? () => {
                setStep('methods')
              }
            : undefined
      }
    />
  )
}

export default RequestPayoutModal
